import { jsx } from 'react/jsx-runtime';
import { createContext, memo, useState, useRef, useCallback, useMemo, useEffect } from 'react';
import _throttle from 'lodash/throttle.js';
import { THROTTLE_SCROLL_DELAY } from '../constants.esm.js';

/**
 *  The Sticky Container houses the context that is used between the StickyElement and Sticky Trigger.
 *  It passes down scroll direction and stickyState object. It also handles the toggling of the keys within the
 *  stickyState object.
 **/
const StickyContainerContext = createContext(null);
const events = [
    "resize",
    "scroll",
    "touchstart",
    "touchmove",
    "touchend",
    "pageshow",
    "load",
];
const hasDom = () => typeof window !== "undefined" && typeof document !== "undefined"; // Polyfill for environments without DOM
const StickyContainer = memo((props) => {
    const [isScrollingUp, setIsScrollingUp] = useState(false); // Help determine scroll direction
    const [hasPassedTrigger, setHasPassedTriggerState] = useState({}); // Holds the hasPassedTrigger state of elements
    const [triggerPositions, setTriggerPositionsState] = useState({}); // Holds the position of triggers
    const isUnMounted = useRef(false); // Used to prevent state updates after unmounting
    const lastScrollPosRef = useRef(0);
    const handleScroll = useCallback((event) => {
        var _a;
        let scrollingElement = (_a = event.target) === null || _a === void 0 ? void 0 : _a.scrollingElement;
        // Polyfill for scrollingElement
        if (hasDom() && !scrollingElement) {
            // Fallback for environments without scrollingElement
            scrollingElement =
                event.currentTarget ||
                    document.documentElement ||
                    document.body;
        }
        const scrollTop = (scrollingElement === null || scrollingElement === void 0 ? void 0 : scrollingElement.scrollTop) || 0;
        if (!isUnMounted.current) {
            if (lastScrollPosRef.current > scrollTop) {
                setIsScrollingUp(true);
            }
            else if (lastScrollPosRef.current < scrollTop) {
                setIsScrollingUp(false);
            }
            lastScrollPosRef.current = scrollTop;
        }
    }, []);
    const throttledHandleScroll = useMemo(() => _throttle(handleScroll, THROTTLE_SCROLL_DELAY, { leading: true }), [handleScroll]);
    useEffect(() => {
        isUnMounted.current = false;
        if (props.addEventListeners && window) {
            events.forEach((event) => window.addEventListener(event, throttledHandleScroll, false));
        }
        return () => {
            if (props.addEventListeners && window) {
                events.forEach((event) => window.removeEventListener(event, throttledHandleScroll, false));
            }
            isUnMounted.current = true;
        };
    }, [props.addEventListeners, throttledHandleScroll]);
    const setHasPassedTrigger = useCallback((newHasPassedTriggerState) => {
        if (!isUnMounted.current) {
            setHasPassedTriggerState((prevState) => (Object.assign(Object.assign({}, prevState), newHasPassedTriggerState)));
        }
    }, []);
    const setTriggerPosition = useCallback((instance, position) => {
        if (!isUnMounted.current) {
            setTriggerPositionsState((prevState) => (Object.assign(Object.assign({}, prevState), { [instance]: position })));
        }
    }, []);
    const providerValue = useMemo(() => ({
        isScrollingUp,
        hasPassedTrigger,
        triggerPositions,
        setHasPassedTrigger,
        setTriggerPosition,
    }), [
        isScrollingUp,
        hasPassedTrigger,
        triggerPositions,
        setHasPassedTrigger,
        setTriggerPosition,
    ]);
    return (jsx("div", { "data-cmp": "stickyContainer", onScroll: handleScroll, onTouchStart: handleScroll, onTouchMove: handleScroll, onTouchEnd: handleScroll, children: jsx(StickyContainerContext.Provider, { value: providerValue, children: props.children }) }));
});
StickyContainer.displayName = "StickyContainer";

export { StickyContainer, StickyContainerContext };
